import React from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@mui/material'

import { EventFilterCard } from '../EventFilterCard'
import { MarketAnalyticsFilterBar } from '../MarketAnalyticsFilterBar'
import { EventCategoryOption, EventTypeOption, TaxonomyCounts } from '../helpers/catalogueUtils'

interface CatalogueFilterBarProps {
  selectedCategoryIds: string[]
  selectedGenreIds: string[]
  selectedSubgenreIds: string[]
  onSetCategories: (ids: string[]) => void
  onSetGenres: (ids: string[]) => void
  onSetSubgenres: (ids: string[]) => void
  onClearCategories: () => void
  onClearGenres: () => void
  onClearSubgenres: () => void
  onClearTaxonomy: () => void
  taxonomyCounts?: TaxonomyCounts
  cohortGamesCount?: number | null
  isCohortLoading?: boolean
  eventCategoryOptions: EventCategoryOption[]
  eventTypeOptions: EventTypeOption[]
  selectedEventCategoryIds: string[]
  selectedEventTypeIds: string[]
  onToggleEventCategory: (id: string) => void
  onToggleEventType: (id: string) => void
  onClearEventCategories: () => void
  onClearEventTypes: () => void
  onClearEvents: () => void
  isLoading?: boolean
}

/**
 * Market Catalogue filter chrome. Stacks the (reused) taxonomy chip bar over
 * `EventFilterCard`, which holds the event-category + event-type chips. Two
 * cards rather than one nested surface keeps the brand's no-nested-cards rule
 * intact.
 *
 * Taxonomy chip counts (category/genre/subgenre) are derived client-side from
 * the unscoped blueprint list (`taxonomyCounts`) rather than `conventional/map`,
 * which exposes no per-node counts. The event-category/type counts in the card
 * below are derived the same way. All counts are blueprint counts.
 */
export const CatalogueFilterBar = React.memo(function CatalogueFilterBar({
  selectedCategoryIds,
  selectedGenreIds,
  selectedSubgenreIds,
  onSetCategories,
  onSetGenres,
  onSetSubgenres,
  onClearCategories,
  onClearGenres,
  onClearSubgenres,
  onClearTaxonomy,
  taxonomyCounts,
  cohortGamesCount,
  isCohortLoading,
  eventCategoryOptions,
  eventTypeOptions,
  selectedEventCategoryIds,
  selectedEventTypeIds,
  onToggleEventCategory,
  onToggleEventType,
  onClearEventCategories,
  onClearEventTypes,
  onClearEvents,
  isLoading = false,
}: CatalogueFilterBarProps) {
  const { t } = useTranslation()

  return (
    <Box role="region" aria-label={t('market-analytics:catalogue_filter_region')}>
      <MarketAnalyticsFilterBar
        selectedCategoryIds={selectedCategoryIds}
        selectedGenreIds={selectedGenreIds}
        selectedSubgenreIds={selectedSubgenreIds}
        onSetCategories={onSetCategories}
        onSetGenres={onSetGenres}
        onSetSubgenres={onSetSubgenres}
        onClearCategories={onClearCategories}
        onClearGenres={onClearGenres}
        onClearSubgenres={onClearSubgenres}
        onClearTaxonomy={onClearTaxonomy}
        taxonomyCounts={taxonomyCounts}
        cohortGamesCount={cohortGamesCount}
        isCohortLoading={isCohortLoading}
      />

      <EventFilterCard
        eventCategoryOptions={eventCategoryOptions}
        eventTypeOptions={eventTypeOptions}
        selectedEventCategoryIds={selectedEventCategoryIds}
        selectedEventTypeIds={selectedEventTypeIds}
        onToggleEventCategory={onToggleEventCategory}
        onToggleEventType={onToggleEventType}
        onClearEventCategories={onClearEventCategories}
        onClearEventTypes={onClearEventTypes}
        onClearEvents={onClearEvents}
        isLoading={isLoading}
        showTypeRowWhenTypeApplied
      />
    </Box>
  )
})

CatalogueFilterBar.displayName = 'CatalogueFilterBar'
